import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, Injector, OnDestroy, OnInit, Signal, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { RemoteConfigState } from '@app/core/firebase/remote-config/remote-config.state';
import { RemoteConfigKeys } from '@app/core/firebase/remote-config/remote-config.types';
import { NavigationVesselPipe } from '@app/core/navigation/navigation.pipe';
import { PermissionService } from '@app/features/admin/settings/permission/permission.service';
import { Permission } from '@app/features/admin/settings/permission/permission.types';
import { SettingsState } from '@app/features/admin/settings/state/settings.state';
import { DownloadManagerComponent } from '@app/layout/common/download-manager/download-manager.component';
import { NotificationComponent } from '@app/layout/common/notification/notification.component';
import { TranslocoModule } from '@ngneat/transloco';
import { PasskeyService } from '@shared/services/passkey/passkey.service';
import {
  SealHorizontalNavigationComponent,
  SealLoadingBarComponent,
  SealMediaWatcherService,
  SealNavigationItem,
  SealNavigationService,
  SealVerticalNavigationComponent
} from '@shipsfocus/seal';
import { ServiceEnum } from 'app/core/constants';
import { NavigationState } from 'app/core/navigation/navigation.state';
import { Navigation } from 'app/core/navigation/navigation.types';
import { ProfileComponent } from 'app/layout/common/profile/profile.component';
import { environment } from 'environments/environment';
import { some } from 'lodash';
import { filter, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterOutlet,
    TranslocoModule,
    MatButtonModule,
    MatIconModule,
    ProfileComponent,
    SealLoadingBarComponent,
    SealVerticalNavigationComponent,
    SealHorizontalNavigationComponent,
    NgClass,
    DownloadManagerComponent,
    NotificationComponent,
    NavigationVesselPipe
  ]
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: WritableSignal<boolean> = signal(false);
  navigation: Signal<Navigation> = computed(() => this._navigationState.navigation());
  horizontalNavigation: WritableSignal<SealNavigationItem[]> = signal([]);

  protected readonly environment: typeof environment = environment;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  protected readonly ServiceEnum: typeof ServiceEnum = ServiceEnum;
  protected readonly router: Router = inject(Router);
  protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  protected readonly passkeyService: PasskeyService = inject(PasskeyService);
  protected readonly remoteConfigState: RemoteConfigState = inject(RemoteConfigState);
  protected readonly remoteConfig: Signal<RemoteConfigKeys> = computed(() => this.remoteConfigState.remoteConfig());
  protected readonly permissionService: PermissionService = inject(PermissionService);
  protected readonly userPermissions: Signal<Permission> = computed(() => this._settingState.userPermissions());

  constructor(
    private _injector: Injector,
    private _sealMediaWatcherService: SealMediaWatcherService,
    private _sealNavigationService: SealNavigationService,
    private _settingState: SettingsState,
    private _navigationState: NavigationState
  ) {}

  ngOnInit(): void {
    effect(
      () => {
        this.updateNavigationVisibility(this.navigation().default);
        this.updateNavigationVisibility(this.navigation().horizontal);
        this.setSelectedHorizontalNavigation();
      },
      { injector: this._injector }
    );

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.setSelectedHorizontalNavigation();
      });

    this._sealMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }: any) => {
      this.isScreenSmall.set(!matchingAliases.includes('md'));
    });
  }

  updateNavigationVisibility(navigation: SealNavigationItem[]) {
    if (this.navigation() && this._settingState.userPermissions()) {
      navigation.forEach((nav) => {
        nav.children?.forEach((child) => {
          const page = child.id.split('.')[1];
          let hasPageAccess: boolean;
          if (this.userPermissions()?.[page]?.['*'] === 0) {
            hasPageAccess = true;
          } else {
            hasPageAccess = this.userPermissions()?.[page]?.['page_access'] === 0;
          }
          if (hasPageAccess) {
            // Even if hasPageAccess, hide nav if it has 'hidden: () => true' property inside the route definition (defaultNavigation)
            child.hidden = child.hidden || (() => false);
          } else {
            child.hidden = () => !hasPageAccess;
          }
        });

        nav.hidden = () => nav.children?.every((child) => child.hidden(child));
      });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  toggleNavigation(name: string): void {
    const navigation = this._sealNavigationService.getComponent<SealVerticalNavigationComponent>(name);
    if (navigation) {
      navigation.toggle();
    }
  }

  private setSelectedHorizontalNavigation() {
    const processedNavigation = [];
    this._navigationState.navigation()?.horizontal?.forEach((n) => {
      const existing = some(n.children, (child) => this.router.url.includes(child.link));
      processedNavigation.push({
        ...n,
        classes: existing
          ? {
              title: 'text-primary'
            }
          : {}
      });
    });
    this.horizontalNavigation.set(processedNavigation);
  }

  onPasskeyLogin() {
    this.passkeyService
      .performLogin(null)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (r) => console.log('[passkey] login verify success', r),
        error: (err) => {
          console.error('[passkey] login verify failed', err);
          console.error('login verify response body:', err?.error || err?.message || err);
        }
      });
  }

  onPasskeyRegister() {
    this.passkeyService
      .performRegister(null)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (r) => console.log('[passkey] register verify success', r),
        error: (err) => console.error('[passkey] register verify failed', err)
      });
  }

  checkPasskey() {
    return this.passkeyService.checkPasskey().pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe();
  }
}
